import { Directive, ElementRef, OnInit, OnDestroy, Renderer2, Input } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

@Directive({
  selector: '[appScrollHint]',
  standalone: true
})
export class ScrollHintDirective implements OnInit, OnDestroy {
  @Input() hintBottom?: string;
  @Input() hintLeft?: string;
  @Input() hintWidth?: string;
  @Input() scrollHintEnabled: boolean = true;
  @Input() mobileOnly = false;
  @Input() hintTheme: 'light' | 'dark' = 'light';

  private scrollSubscription?: Subscription;
  private mutationObserver?: MutationObserver;
  private hintDismissed = false;
  private hintElement?: HTMLElement;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    if (!this.scrollHintEnabled) return;
    if (this.mobileOnly && !this.isMobile()) return;

    setTimeout(() => {
      if (!this.hintBottom) this.hintBottom = '70px'; // Button + padding height
      if (!this.hintLeft) this.hintLeft = '0';
      if (!this.hintWidth) this.hintWidth = '100%';

      this.createHintElement();
      this.setupScrollListener();
      this.setupMutationObserver();
      this.updateHintVisibility();
    });
  }

  private setupMutationObserver() {
    this.mutationObserver = new MutationObserver(() => this.updateHintVisibility());
    this.mutationObserver.observe(this.el.nativeElement, { childList: true, subtree: true });
  }

  private isMobile(): boolean {
    return window.innerWidth <= 600;
  }

  private createHintElement() {
    const host = this.el.nativeElement;
    const parent = host.parentElement;
    if (!parent) return;

    // Create hint container
    this.hintElement = this.renderer.createElement('div');
    this.renderer.addClass(this.hintElement, 'scroll-hint-overlay');
    this.renderer.setStyle(this.hintElement, 'position', 'absolute');
    this.renderer.setStyle(this.hintElement, 'bottom', this.hintBottom);
    this.renderer.setStyle(this.hintElement, 'left', this.hintLeft);
    this.renderer.setStyle(this.hintElement, 'right', '0');
    this.renderer.setStyle(this.hintElement, 'width', this.hintWidth);
    this.renderer.setStyle(this.hintElement, 'height', '3rem');
    this.renderer.setStyle(this.hintElement, 'display', 'flex');
    this.renderer.setStyle(this.hintElement, 'align-items', 'flex-end');
    this.renderer.setStyle(this.hintElement, 'justify-content', 'center');
    this.renderer.setStyle(this.hintElement, 'pointer-events', 'none');
    this.renderer.setStyle(this.hintElement, 'opacity', '0');
    this.renderer.setStyle(this.hintElement, 'transition', 'opacity 0.3s ease');
    this.renderer.setStyle(this.hintElement, 'z-index', '5');

    // Create gradient
    const gradient: HTMLElement = this.renderer.createElement('div');
    this.renderer.setStyle(gradient, 'position', 'absolute');
    this.renderer.setStyle(gradient, 'top', '0');
    this.renderer.setStyle(gradient, 'left', '0');
    this.renderer.setStyle(gradient, 'right', '0');
    this.renderer.setStyle(gradient, 'bottom', '0');
    this.renderer.setStyle(
      gradient,
      'background',
      `linear-gradient(to bottom, rgba(255, 255, 255, 0), ${this.hintTheme === 'dark' ? 'var(--dark-base-color)' : '#fff'})`
    );
    this.renderer.appendChild(this.hintElement, gradient);

    // Create arrow
    const arrow: HTMLElement = this.renderer.createElement('mat-icon');
    this.renderer.addClass(arrow, 'material-icons');
    this.renderer.setStyle(arrow, 'font-size', '2rem');
    this.renderer.setStyle(arrow, 'color', 'var(--gray-input-color)');
    this.renderer.setStyle(arrow, 'animation', 'scroll-hint-bounce 1.5s infinite');
    this.renderer.setStyle(arrow, 'z-index', '1');
    const text = this.renderer.createText('keyboard_arrow_down');
    this.renderer.appendChild(arrow, text);
    this.renderer.appendChild(this.hintElement, arrow);

    // Add to DOM
    this.renderer.insertBefore(parent, this.hintElement, host.nextSibling);

    this.addAnimationStyles();
  }

  private addAnimationStyles() {
    const styleId = 'scroll-hint-animation-styles';
    if (document.getElementById(styleId)) return;

    const style = this.renderer.createElement('style');
    this.renderer.setAttribute(style, 'id', styleId);
    const css = `
      @keyframes scroll-hint-bounce {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(5px); }
      }
    `;
    this.renderer.appendChild(style, this.renderer.createText(css));
    this.renderer.appendChild(document.head, style);
  }

  private setupScrollListener() {
    const el = this.el.nativeElement;

    this.scrollSubscription = fromEvent(el, 'scroll')
      .pipe(map(() => this.shouldShowHint(el)))
      .subscribe((show) => {
        this.setHintVisibility(show);
      });
  }

  private shouldShowHint(el: HTMLElement): boolean {
    const hasScroll = el.scrollHeight > el.clientHeight;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 5;
    if (hasScroll && atBottom) this.hintDismissed = true;

    return hasScroll && !atBottom && !this.hintDismissed;
  }

  private updateHintVisibility() {
    const el: HTMLElement = this.el.nativeElement;
    const show = this.shouldShowHint(el);
    this.setHintVisibility(show);
  }

  private setHintVisibility(show: boolean) {
    if (!this.hintElement) return;
    this.renderer.setStyle(this.hintElement, 'opacity', show ? '1' : '0');
  }

  ngOnDestroy() {
    this.scrollSubscription?.unsubscribe();
    this.mutationObserver?.disconnect();
    if (this.hintElement) {
      this.renderer.removeChild(this.hintElement.parentElement, this.hintElement);
    }
  }
}
